import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Target, HeartHandshake, Sparkles, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Reveal, SectionHeading, Eyebrow } from "../components/common";
import Seo from "../components/Seo";
import DiscoverBanner from "../components/DiscoverBanner";

const values = [
  { icon: "Sparkles", title: "Science, honestly applied", desc: "Every insight we share is grounded in validated, standardized cognitive science, never guesswork or labels." },
  { icon: "HeartHandshake", title: "Development, not judgement", desc: "We assess to help children grow, giving parents and teachers a clear path forward, not a verdict." },
  { icon: "Target", title: "The right age, the right time", desc: "We help families act early, when small, well timed support compounds into lifelong advantage." },
];

const iconMap = { Compass, Target, HeartHandshake, Sparkles };

export default function AboutUs() {
  return (
    <div className="overflow-hidden">
      <Seo
        title="Our Mission & Vision | MapMyKid"
        description="MapMyKid helps every child discover their natural abilities early and develop them fast. Read our mission and vision for cognitive assessment and personalised development from Nursery to Grade 12."
      />

      <section className="relative hero-gradient pt-32 sm:pt-40 pb-14">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center animate-fade-up">
          <Eyebrow>About MapMyKid</Eyebrow>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.06] text-slate-900">
            Beyond marks, <span className="brand-gradient-text">to the mind.</span>
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            We believe every child is intelligent in their own way. Our purpose is to make that intelligence visible, and help it grow.
          </p>
        </div>
      </section>

      {/* MISSION */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <Reveal>
            <span className="grid h-14 w-14 place-items-center rounded-2xl cta-gradient text-white shadow-lg shadow-violet-300/40"><Compass className="h-7 w-7" /></span>
            <h2 className="mt-5 text-3xl sm:text-4xl font-extrabold text-slate-900">Our Mission</h2>
            <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-600">
              Our mission is to help every child discover their natural abilities early and develop them fast. Through advanced cognitive science and clear, data driven insight, we empower parents, schools and educators with a true understanding of how each child thinks, learns and grows, so the right support can begin at the right age and the right time.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="rounded-3xl border border-violet-100 bg-violet-50/40 p-7 sm:p-9">
              <p className="text-sm font-bold uppercase tracking-wide text-violet-600">What we help you see</p>
              <ul className="mt-5 space-y-3.5">
                {["Intellect and memory (Dynamic IQ)", "Focus and concentration", "Decision making speed", "Creativity acumen", "Natural ability and strengths", "Multiple intelligence and learning style"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-base text-slate-700"><Check className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" /> {t}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VISION */}
      <section className="section-pad soft-gradient">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:order-2">
            <span className="grid h-14 w-14 place-items-center rounded-2xl cta-gradient text-white shadow-lg shadow-violet-300/40"><Target className="h-7 w-7" /></span>
            <h2 className="mt-5 text-3xl sm:text-4xl font-extrabold text-slate-900">Our Vision</h2>
            <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-600">
              To become a trusted global platform for cognitive assessment and personalised development, recognised for scientific rigour, honesty and a real, lasting impact on children's lives. We aspire to move education beyond marks, towards the mind, so that every child grows into the most capable and confident version of themselves.
            </p>
          </Reveal>
          <Reveal delay={100} className="lg:order-1">
            <div className="rounded-3xl border border-violet-100 bg-white p-7 sm:p-9 shadow-xl shadow-violet-100/50">
              <p className="text-base leading-relaxed text-slate-600">
                MapMyKid brings together cognitive scientists, educators and technologists with one shared belief: understanding a child's mind is the most powerful gift we can give them. From Nursery to Grade 12, we turn deep science into a warm, practical tool that any family or school can use.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="What We Stand For" title="The principles behind every assessment." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v, i) => {
              const Icon = iconMap[v.icon] || Sparkles;
              return (
                <Reveal key={v.title} delay={i * 90}>
                  <div className="group h-full rounded-3xl border border-violet-100 bg-white p-7 card-hover">
                    <span className="grid h-14 w-14 place-items-center rounded-2xl bg-violet-50 text-violet-600 transition-colors group-hover:cta-gradient group-hover:text-white"><Icon className="h-7 w-7" /></span>
                    <h3 className="mt-4 text-xl font-bold text-slate-900">{v.title}</h3>
                    <p className="mt-2 text-base text-slate-600 leading-relaxed">{v.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-12 flex flex-col sm:flex-row justify-center gap-3.5">
            <Link to="/research"><Button variant="outline" className="rounded-full h-14 px-7 text-base font-semibold border-violet-200 text-violet-700 hover:bg-violet-50 w-full sm:w-auto">See our research</Button></Link>
            <Link to="/book"><Button className="cta-gradient text-white rounded-full h-14 px-7 text-base font-semibold w-full sm:w-auto">Book an assessment <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
          </div>
        </div>
      </section>

      <DiscoverBanner />
    </div>
  );
}
